import type { ComponentType, ReactNode } from 'react';
import type { IconProps } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';
import type { ChartLayout } from './layout';

export type PictogramProps = {
  /** The icon one unit is drawn with (Phosphor). */
  icon: ComponentType<IconProps>;
  /** How many units to draw (round the value to whole units: 19,391 jobs at 500 a unit is 39). */
  units: number;
  /** How many of them are the finding (sun); the rest context. */
  highlight?: number;
  /** What one icon stands for ("= 500 open jobs"). */
  unitLabel: ReactNode;
  /** One sentence with the real figure, for screen readers. */
  description: string;
  /** `auto` (default) switches to the narrow form in a narrow container: smaller icons and tighter gaps. */
  layout?: ChartLayout;
  className?: string;
};

/**
 * A count drawn as rows of icons, each standing for a round number: for data posts and explainers,
 * where "39 people" reads faster than a bar. At most 100 icons; the exact figure is in words.
 */
const ICONS: Record<ChartLayout, string> = {
  auto: 'flex flex-wrap gap-0.5 @md:gap-1',
  narrow: 'flex flex-wrap gap-0.5',
  wide: 'flex flex-wrap gap-1',
};
const ICON: Record<ChartLayout, string> = { auto: '@max-md:size-4', narrow: '', wide: '' };

export function Pictogram({
  icon,
  units,
  highlight = 0,
  unitLabel,
  layout = 'auto',
  description,
  className,
}: PictogramProps) {
  const n = Math.max(0, Math.min(100, Math.round(units)));
  return (
    <div className={cx('@container flex flex-col gap-2', className)}>
      <div role="img" aria-label={description} className={ICONS[layout]}>
        {Array.from({ length: n }, (_, i) => (
          <Icon
            key={i}
            icon={icon}
            size={layout === 'narrow' ? 'sm' : 'md'}
            weight="fill"
            className={cx(ICON[layout], i < highlight ? 'text-jc-chart-2' : 'text-jc-chart-context')}
          />
        ))}
      </div>
      <span className="inline-flex items-center gap-1.5 type-meta text-jc-ink-secondary" aria-hidden="true">
        <Icon icon={icon} size="sm" weight="fill" className="text-jc-chart-context" />
        {unitLabel}
      </span>
    </div>
  );
}
